import {Box, Typography} from "@mui/material";
import {useState} from "react";
import {useRecoilValue} from "recoil";
import {CurrentUserState} from "../../../atoms/CurrentUser";

type OrderItemLabelsProps = {
   itemIds: string[]
   name: string
}

const getItemLabelsPDF = async (itemIds: string[], accessToken: string): Promise<Blob> => {
   try {
      // Create URL with query parameters for the itemIds
      const url = new URL('https://create2stay-functions2.azurewebsites.net/api/internallogistics/itemlabel');
      url.searchParams.set("ids", itemIds.join(','));

      const response = await fetch(url.toString(), {
         method: 'GET',
         headers: {
            'Content-Type': 'application/json',
            'authorization': `Bearer ${accessToken}`
         }
      });

      if (!response.ok) {
         throw new Error(`Failed to get item labels: ${response.statusText}`);
      }

      // Get the response as a blob since it's a PDF
      return await response.blob();
   } catch (error) {
      console.error('Error getting item labels:', error);
      throw error;
   }
};

export const OrderItemLabels = ({ name, itemIds }: OrderItemLabelsProps) => {
   const currentUser = useRecoilValue(CurrentUserState);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const accessToken = currentUser?.token;
   if (!accessToken) {
      return <Box>Access token is missing</Box>
   }

   const handleDownload = async (e:any) => {
      e.preventDefault();
      e.stopPropagation();
      setIsLoading(true);
      setError(null);

      try {
         const pdfBlob = await getItemLabelsPDF(itemIds, accessToken);

         // Create a URL for the blob
         const url = window.URL.createObjectURL(pdfBlob);

         // Create a temporary link element to trigger the download
         const link = document.createElement('a');
         link.href = url;
         link.download = `${name}-${new Date().toISOString().split('T')[0]}.pdf`;
         document.body.appendChild(link);

         // Trigger the download
         link.click();

         // Clean up
         window.URL.revokeObjectURL(url);
         document.body.removeChild(link);
      } catch (err) {
         setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Box>
         <a href={"#/"}
            onClick={handleDownload}
         >
            {isLoading ? 'Downloading...' : 'Download'}
         </a>

         {error && (
            <Typography color="error" sx={{ mt: 1 }}>
               {error}
            </Typography>
         )}

         {itemIds.length === 0 && (
            <Typography sx={{ mt: 1 }}>
               No items selected for download
            </Typography>
         )}
      </Box>
   );
}